import { useEffect, useMemo, useState } from 'react'

import { useCommercialStructure } from 'commons/customHooks'
import { formatDate } from 'commons/utils/date'
import {
  ActionTabs,
  Badge,
  BatchSearch,
  Flex,
  Icon,
  type ItemActionTabsType,
  LoadingHavaianas,
  NotFound,
  Select,
  SlimButton,
  Tooltip,
} from 'components'
import { availableCentersFactories, monthOptions, type Offices } from 'constants/enums'
import { t } from 'i18next'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { fetchSharesOffice, setPage } from 'store/FairShareContext/fairShareSlice'
import { getCommercialStructure } from 'store/SharedContext/sharedSlice'
import { useDispatch, useSelector } from 'store/store'

import * as S from '../../styles'
import { EmptyShare } from '../EmptyShare'
import { OfficeTable } from '../OfficeTable'

export const OfficeView = () => {
  const dispatch = useDispatch()
  const { getBMUs, getRegionals, getZones, getRegionalsByOffices, getZonesByRegionals } =
    useCommercialStructure()
  const { hasCommercialStructure } = useSelector((state) => state.shared)

  const {
    loadingOfficeView,
    loadingShareUpload,
    loadingMetaUpload,
    hasOptionalFilters,
    officeFilters,
    sharesOffice: { offices, data },
    pagination: { currentPage, pageSize },
  } = useSelector((state) => state.fairShare)
  const [selectedOfficeCode, setSelectedOfficeCode] = useState('')

  const defaultValues = {
    businessUnit: '',
    center: '',
    color: '',
    material: '',
    month: formatDate(new Date(), 'MM'),
    office: '',
    regional: '',
    year: formatDate(new Date(), 'YYYY'),
    zone: '',
  }

  const methods = useForm({
    defaultValues,
  })

  const { handleSubmit, reset } = methods

  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const year = (currentYear + i).toString()
    return {
      title: year,
      value: year,
    }
  })

  const centerFactoryOptions = [
    { title: t('common.all', { context: 'male' }), value: '' },
    ...availableCentersFactories.map((center) => ({
      title: center,
      value: center,
    })),
  ]

  const { watch, setValue, getValues } = methods
  const businessUnit = watch('businessUnit')
  const selectedOffice = watch('office')
  const selectedRegional = watch('regional')

  const regionalOptions = useMemo(() => {
    let regionals = []
    if (selectedOffice) {
      regionals = getRegionalsByOffices([selectedOffice] as Offices[])
    } else {
      regionals = getRegionals()
    }

    const uniqueRegionals = Array.from(new Map(regionals.map((r) => [r.id, r])).values())

    const options = uniqueRegionals.map((r) => ({
      title: `${r.id} - ${r.description}`,
      value: r.id,
    }))

    if (selectedOffice) {
      return [{ title: t('common.all', { context: 'female' }), value: '' }, ...options]
    }
    return options
  }, [selectedOffice, getRegionalsByOffices, getRegionals])

  const zoneOptions = useMemo(() => {
    let zones = []
    if (selectedRegional) {
      zones = getZonesByRegionals([selectedRegional])
    } else {
      zones = getZones()
    }

    const uniqueZones = Array.from(new Map(zones.map((z) => [z.id, z])).values())

    const options = uniqueZones.map((z) => ({
      title: `${z.id} - ${z.description}`,
      value: z.id,
    }))
    if (selectedRegional) {
      return [{ title: t('common.all', { context: 'female' }), value: '' }, ...options]
    }
    return options
  }, [selectedRegional, getZonesByRegionals, getZones])

  const getPayload = () => {
    const { year, month, businessUnit, center, material, color, office, regional, zone } =
      getValues()

    return {
      businessUnit,
      centerCode: center,
      colorCodes: color ? color.split(',') : [],
      currentPage: currentPage - 1,
      materialCodes: material ? material.split(',') : [],
      officeCode: office,
      pageSize: pageSize,
      regionCode: zone,
      teamCode: regional,
      yearMonth: Number(year + month),
    }
  }

  const bmus = getBMUs()
  const bmuOptions = useMemo(
    () =>
      bmus.map((bmu) => ({
        title: bmu.bmuName,
        value: bmu.bmuCode,
      })),
    [bmus],
  )

  const clearOfficeFilters = () => {
    setSelectedOfficeCode('')
    setValue('office', '')
    setValue('regional', '')
    setValue('zone', '')
  }

  useEffect(() => {
    if (!hasCommercialStructure) dispatch(getCommercialStructure())
  }, [hasCommercialStructure])

  useEffect(() => {
    if (bmuOptions.length) {
      setValue('businessUnit', bmuOptions[0].value)
    }
  }, [bmuOptions])

  const hasBusinessUnit = useMemo(() => !!businessUnit, [businessUnit])

  useEffect(() => {
    if (!hasBusinessUnit) return

    const fairSharePayload = getPayload()
    dispatch(fetchSharesOffice(fairSharePayload))
  }, [currentPage, pageSize, hasBusinessUnit])

  useEffect(() => {
    if (selectedOfficeCode === '') return

    const isValidOffice = offices.some((o) => o.officeCode === selectedOfficeCode)
    if (!isValidOffice) {
      clearOfficeFilters()
      dispatch(fetchSharesOffice({ ...getPayload(), currentPage: 0, officeCode: '' }))
    }
  }, [offices])

  const currentOfficeTab = useMemo(() => {
    if (!selectedOfficeCode) return '0'
    const idx = offices.findIndex((o) => o.officeCode === selectedOfficeCode)
    return idx === -1 ? '0' : `${idx + 1}`
  }, [selectedOfficeCode, offices])

  const handleTabChange = (officeCode: string) => {
    dispatch(setPage(1))
    setSelectedOfficeCode(officeCode)
    setValue('office', officeCode)
    setValue('regional', '')
    setValue('zone', '')

    const fairSharePayload = { ...getPayload(), currentPage: 0 }
    dispatch(fetchSharesOffice(fairSharePayload))
  }

  const getTabItems = () => {
    const items: ItemActionTabsType[] = [
      {
        label: t('common.all', { context: 'male' }),
        onClick: () => handleTabChange(''),
        status: offices.every((office) => office.frozenTarget) ? 'success' : 'danger',
      },
    ]
    if (offices.length) {
      offices.forEach((office) => {
        items.push({
          label: office.officeCode,
          onClick: () => handleTabChange(office.officeCode),
          status: office.frozenTarget ? 'success' : 'danger',
        })
      })
    }
    return items
  }

  const handleSearch = () => {
    dispatch(setPage(1))

    // NOTE: clear office related filters if businessUnit has changed
    if (officeFilters && officeFilters.businessUnit !== getValues('businessUnit')) {
      clearOfficeFilters()
    }

    const fairSharePayload = {
      ...getPayload(),
      currentPage: 0,
    }
    dispatch(fetchSharesOffice(fairSharePayload))
  }

  const handleClear = () => {
    reset({ ...defaultValues, businessUnit: bmuOptions[0].value })
    dispatch(setPage(1))
    setSelectedOfficeCode('')
    const fairSharePayload = {
      ...getPayload(),
      currentPage: 0,
    }
    dispatch(fetchSharesOffice(fairSharePayload))
  }

  const checkedCount = useMemo(() => {
    return data.filter((item) => item.checked).length
  }, [data])

  const { openOfficeCount, closedOfficeCount } = useMemo(() => {
    const openCount = offices.filter((office) => office.frozenTarget).length
    const closedCount = offices.filter((office) => !office.frozenTarget).length
    return { closedOfficeCount: closedCount, openOfficeCount: openCount }
  }, [offices])

  const getSelectMenuText = (selectKey?: 'regional' | 'zone') => {
    if (!data.length && !hasOptionalFilters) {
      return t('screens.fairShare.loadTheShare')
    }
    if (selectKey === 'regional' && !selectedOffice) {
      return t('screens.fairShare.selectAnOffice')
    }
    if (selectKey === 'zone' && !selectedRegional) {
      return t('screens.fairShare.selectARegional')
    }
    return t('common.placeholders.select')
  }

  return (
    <FormProvider {...methods}>
      <S.Form onSubmit={handleSubmit(handleSearch)}>
        {loadingOfficeView ? (
          <Flex>
            <LoadingHavaianas />
          </Flex>
        ) : (
          <Flex direction="column" gap="xm">
            <S.GridContainerOffice>
              <S.FieldWrapper className="grid-1-4">
                <Controller
                  name="businessUnit"
                  render={({ field: { name, value, onChange } }) => (
                    <Select
                      disabled={!bmuOptions.length}
                      id="businessUnit"
                      label={t('common.bmu')}
                      name={name}
                      onChange={(e) => onChange(e)}
                      options={bmuOptions}
                      value={value}
                    />
                  )}
                />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-4-7">
                <Controller
                  name="month"
                  render={({ field: { name, value, onChange } }) => (
                    <Select
                      id="month"
                      label={t('common.month')}
                      name={name}
                      onChange={(e) => onChange(e)}
                      options={monthOptions.map((option) => ({
                        ...option,
                        title: t(option.title),
                      }))}
                      value={value}
                    />
                  )}
                />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-7-10">
                <Controller
                  name="year"
                  render={({ field: { name, value, onChange } }) => (
                    <Select
                      id="year"
                      label={t('common.year')}
                      name={name}
                      onChange={(e) => onChange(e)}
                      options={yearOptions}
                      value={value}
                    />
                  )}
                />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-10-13">
                <Controller
                  name="center"
                  render={({ field: { name, value, onChange } }) => (
                    <Select
                      disabled={
                        !centerFactoryOptions.length || (!data.length && !hasOptionalFilters)
                      }
                      id="center"
                      label={t('common.center')}
                      menuText={getSelectMenuText()}
                      name={name}
                      onChange={(e) => onChange(e)}
                      options={centerFactoryOptions}
                      value={value}
                    />
                  )}
                />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-13-19">
                <Controller
                  name="regional"
                  render={({ field }) => (
                    <Select
                      disabled={
                        !selectedOffice ||
                        !regionalOptions.length ||
                        (!data.length && !hasOptionalFilters)
                      }
                      hasSearch
                      label={t('common.regional')}
                      menuText={getSelectMenuText('regional')}
                      name={field.name}
                      onChange={(e) => {
                        field.onChange(e)
                        setValue('zone', '')
                      }}
                      options={regionalOptions}
                      value={field.value}
                    />
                  )}
                />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-19-25">
                <Controller
                  name="zone"
                  render={({ field }) => (
                    <Select
                      disabled={
                        !selectedRegional ||
                        !zoneOptions.length ||
                        (!data.length && !hasOptionalFilters)
                      }
                      hasSearch
                      label={t('common.zone')}
                      menuText={getSelectMenuText('zone')}
                      name={field.name}
                      onChange={(value) => field.onChange(value)}
                      options={zoneOptions}
                      value={field.value}
                    />
                  )}
                />
              </S.FieldWrapper>

              <S.FieldWrapper className="grid-1-13">
                <Controller
                  name="material"
                  render={({ field: { value, onChange } }) => (
                    <BatchSearch
                      disabled={!data.length && !hasOptionalFilters}
                      label={t('common.material')}
                      onChange={(e) => onChange(e)}
                      onClear={() => onChange('')}
                      placeHolder={
                        data.length ? t('common.searchByCode') : t('screens.fairShare.loadTheShare')
                      }
                      value={value}
                    />
                  )}
                />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-13-22">
                <Controller
                  name="color"
                  render={({ field: { value, onChange } }) => (
                    <BatchSearch
                      disabled={!data.length && !hasOptionalFilters}
                      label={t('common.color')}
                      onChange={(e) => onChange(e)}
                      onClear={() => onChange('')}
                      placeHolder={
                        data.length ? t('common.searchByCode') : t('screens.fairShare.loadTheShare')
                      }
                      value={value}
                    />
                  )}
                />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-22-24">
                <SlimButton content={t('buttons.search')} type="submit" variant="primary" />
              </S.FieldWrapper>
              <S.FieldWrapper className="grid-24-25">
                <Tooltip arrow title={t('buttons.clear')}>
                  <Flex fullWidth>
                    <SlimButton
                      icon={<Icon icon="close" size="20" />}
                      onClick={handleClear}
                      type="button"
                      variant="border"
                    />
                  </Flex>
                </Tooltip>
              </S.FieldWrapper>
            </S.GridContainerOffice>

            {!!offices.length && (
              <S.OfficeTabsWrapper>
                <ActionTabs currentItem={currentOfficeTab} items={getTabItems()} />
                <Flex gap="sm">
                  {!!checkedCount && (
                    <Badge appearance="solid" color="info">
                      {t('common.itemSelected', {
                        count: checkedCount,
                      })}
                    </Badge>
                  )}
                  {!!openOfficeCount && (
                    <Badge appearance="soft" color="success">
                      {t('screens.fairShare.walletOpen', {
                        count: openOfficeCount,
                      })}
                    </Badge>
                  )}
                  {!!closedOfficeCount && (
                    <Badge appearance="soft" color="danger">
                      {t('screens.fairShare.walletClosed', {
                        count: closedOfficeCount,
                      })}
                    </Badge>
                  )}
                </Flex>
              </S.OfficeTabsWrapper>
            )}

            {data.length ? (
              <OfficeTable />
            ) : !hasOptionalFilters ? (
              <EmptyShare
                descriptionKey="office"
                loadingUpload={loadingShareUpload || loadingMetaUpload}
              />
            ) : (
              <NotFound
                description={
                  <Flex alignItems="center" direction="column" gap="xm">
                    {t('common.messages.noResultFromFilters')}
                    <SlimButton
                      content={t('buttons.clearFilters')}
                      icon={<Icon icon="close" size="18" />}
                      onClick={handleClear}
                      type="button"
                      variant="info"
                    />
                  </Flex>
                }
                icon={<Icon icon="bot" size="40" />}
                title={t('common.messages.opsNotFound')}
              />
            )}
          </Flex>
        )}
      </S.Form>
    </FormProvider>
  )
}
